import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText } from "./api";

const EMPTY_TEAM = { name: "", role: "", bio: "", image_url: "", display_order: 1, active: true };
const EMPTY_SERVICE = { name: "", copy: "", detail: "", display_order: 1, active: true };

const Form = ({ kind, initial, onDone, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isTeam = kind === "team";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, display_order: Number(form.display_order) || 0 };
      if (form.id) await adminApi.put(`/admin/${kind}/${form.id}`, payload);
      else await adminApi.post(`/admin/${kind}`, payload);
      toast.success(`${isTeam ? "Team member" : "Service"} saved. The website is updated.`);
      onDone();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="admin-panel ts-form" onSubmit={submit} data-testid={`${kind}-form`}>
      <h2>{form.id ? "Edit" : "Add"} {isTeam ? "team member" : "service"}</h2>
      <div className="form-row">
        <label>{isTeam ? "Name" : "Service name"}<input required maxLength={120} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid={`${kind}-name-input`} /></label>
        {isTeam ? (
          <label>Role<input required maxLength={120} value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="team-role-input" /></label>
        ) : (
          <label>Display order<input type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} data-testid="services-order-input" /></label>
        )}
      </div>
      <label>{isTeam ? "Short bio" : "Short description"}<textarea rows={2} required maxLength={500} value={isTeam ? form.bio : form.copy} onChange={(e) => set(isTeam ? "bio" : "copy", e.target.value)} data-testid={`${kind}-description-input`} /></label>
      {isTeam ? (
        <div className="form-row">
          <label>Image URL <small>(paste from Media Manager)</small><input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://... or /api/media/..." data-testid="team-image-input" /></label>
          <label>Display order<input type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} data-testid="team-order-input" /></label>
        </div>
      ) : (
        <label>Detailed description (optional)<textarea rows={2} maxLength={1000} value={form.detail} onChange={(e) => set("detail", e.target.value)} data-testid="services-detail-input" /></label>
      )}
      <label className="ts-toggle">
        <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid={`${kind}-active-checkbox`} />
        Visible on the public website
      </label>
      <div className="admin-actions">
        <button type="submit" className="btn btn-primary" disabled={saving} data-testid={`${kind}-save-button`}>{saving ? "Saving…" : "Save"}</button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} data-testid={`${kind}-cancel-button`}><X size={15} /> Cancel</button>
      </div>
    </form>
  );
};

export default function TeamServices() {
  const [tab, setTab] = useState("team");
  const [team, setTeam] = useState(null);
  const [services, setServices] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => {
    adminApi.get("/admin/team").then((r) => setTeam(r.data)).catch(() => toast.error("Could not load team."));
    adminApi.get("/admin/services").then((r) => setServices(r.data)).catch(() => toast.error("Could not load services."));
  };
  useEffect(() => { load(); }, []);

  const remove = async (kind, item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/${kind}/${item.id}`);
      toast.success("Deleted. The website is updated.");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const list = tab === "team" ? team : services;

  return (
    <div data-testid="team-services">
      <div className="admin-page-head">
        <h1>Team & Services</h1>
        <p>Manage the people and services shown on the public website.</p>
      </div>
      <div className="admin-toolbar">
        <div className="filters">
          <button className={tab === "team" ? "selected" : ""} onClick={() => { setTab("team"); setEditing(null); }} data-testid="ts-tab-team">Team Members</button>
          <button className={tab === "services" ? "selected" : ""} onClick={() => { setTab("services"); setEditing(null); }} data-testid="ts-tab-services">Services</button>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing(tab === "team" ? { ...EMPTY_TEAM } : { ...EMPTY_SERVICE })} data-testid="ts-add-button">
          <Plus size={15} /> Add {tab === "team" ? "member" : "service"}
        </button>
      </div>
      {editing && <Form kind={tab} initial={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      {!list && <p className="admin-loading">Loading…</p>}
      {list && list.length === 0 && <p className="empty-note" data-testid="ts-empty">Nothing here yet. Add your first {tab === "team" ? "team member" : "service"}.</p>}
      <div className="ts-list">
        {(list || []).map((item) => (
          <div className="admin-card ts-row" key={item.id} data-testid={`ts-row-${tab}`}>
            {tab === "team" && item.image_url && <img className="ts-avatar" src={item.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url} alt={item.name} />}
            <div className="ts-info">
              <strong>{item.name}</strong>
              <span>{tab === "team" ? item.role : item.copy}</span>
              <small>Order {item.display_order} · {item.active ? "Visible" : "Hidden"}</small>
            </div>
            <div className="admin-actions">
              <button className="icon-btn" onClick={() => setEditing(item)} aria-label={`Edit ${item.name}`} data-testid="ts-edit-button"><Pencil size={15} /></button>
              <button className="icon-btn danger" onClick={() => remove(tab, item)} aria-label={`Delete ${item.name}`} data-testid="ts-delete-button"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
